import { FormEvent, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { DokumentInput, createDokument, deleteDokument, fetchDokumente } from "@/lib/api"
import { useToast } from "@/components/Toast"
import { SkeletonZeile } from "@/components/Skeleton"
import { EmptyState } from "@/components/EmptyState"
import { formatDatum } from "@/lib/format"
import { Button, Input, Select } from "@/components/ui"
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"

const KATEGORIE_LABELS: Record<string, string> = {
  dokument: "Dokument",
  datei: "Datei",
  zeichnung: "Zeichnung",
}

const LEER: DokumentInput = { kategorie: "dokument", bezeichnung: "" }

/**
 * Dokumente-Sektion (Paperless-Verweis) innerhalb der Organisation-
 * Detailseite (Ninox `JC`+`AD`+`LE`, konsolidiert) — AP-2.7.
 */
export function DokumenteSection({ organisationId }: { organisationId: string }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [neu, setNeu] = useState<DokumentInput>(LEER)
  const [zeigeNeu, setZeigeNeu] = useState(false)
  const [loeschenId, setLoeschenId] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ["dokumente", "organisation", organisationId],
    queryFn: () => fetchDokumente({ organisationId }),
  })

  const invalidieren = () =>
    queryClient.invalidateQueries({ queryKey: ["dokumente", "organisation", organisationId] })

  const anlegen = useMutation({
    mutationFn: (payload: DokumentInput) => createDokument({ ...payload, organisationId }),
    onSuccess: () => {
      invalidieren()
      setNeu(LEER)
      setZeigeNeu(false)
      toast.show("Dokument angelegt.", "success")
    },
    onError: () => toast.show("Dokument konnte nicht angelegt werden.", "error"),
  })

  const loeschen = useMutation({
    mutationFn: (id: string) => deleteDokument(id),
    onSuccess: () => {
      invalidieren()
      setLoeschenId(null)
      toast.show("Dokument gelöscht.", "success")
    },
    onError: () => toast.show("Dokument konnte nicht gelöscht werden.", "error"),
  })

  function onSubmitNeu(e: FormEvent) {
    e.preventDefault()
    anlegen.mutate(neu)
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Dokumente</h2>
        <button
          type="button"
          onClick={() => setZeigeNeu(true)}
          className="rounded-md border border-slate-300 px-3 py-1 text-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
        >
          + Dokument hinzufügen
        </button>
      </div>

      {isLoading && (
        <div className="space-y-2">
          <SkeletonZeile className="h-10 w-full" />
        </div>
      )}

      <ul className="space-y-2">
        {data?.map((d) => (
          <li
            key={d.id}
            className="flex items-center justify-between rounded-md border border-slate-100 p-3 text-sm"
          >
            <div>
              <p className="font-medium text-slate-900">
                {d.bezeichnung ?? d.dokumentNr ?? "Ohne Bezeichnung"}
              </p>
              <p className="text-slate-500">
                {KATEGORIE_LABELS[d.kategorie] ?? d.kategorie}
                {d.datum ? ` · ${formatDatum(d.datum)}` : ""}
                {!d.paperlessId && " · noch nicht in Paperless hochgeladen"}
              </p>
            </div>
            {loeschenId === d.id ? (
              <span className="flex items-center gap-1.5 text-xs">
                <span className="text-slate-600">Wirklich löschen?</span>
                <button
                  type="button"
                  onClick={() => loeschen.mutate(d.id)}
                  disabled={loeschen.isPending}
                  className="rounded-md bg-red-600 px-2 py-1 text-white hover:bg-red-700 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                >
                  Ja, löschen
                </button>
                <button
                  type="button"
                  onClick={() => setLoeschenId(null)}
                  className="rounded-md border border-slate-300 px-2 py-1 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                >
                  Abbrechen
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setLoeschenId(d.id)}
                className="rounded-md border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
              >
                Löschen
              </button>
            )}
          </li>
        ))}
      </ul>

      {!isLoading && (data?.length ?? 0) === 0 && !zeigeNeu && (
        <EmptyState
          text="Noch keine Dokumente hinterlegt."
          aktion={{ label: "+ Dokument hinzufügen", onClick: () => setZeigeNeu(true) }}
        />
      )}

      <Sheet open={zeigeNeu} onOpenChange={setZeigeNeu}>
        <SheetContent sheetKey="dokument-anlegen" className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Neues Dokument</SheetTitle>
            <SheetDescription>Dokument-Verweis für diese Organisation erfassen.</SheetDescription>
          </SheetHeader>
          <form id="dokument-neu-form" onSubmit={onSubmitNeu} className="grid gap-4 py-4">
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-slate-700">Kategorie</label>
              <Select
                value={neu.kategorie}
                onChange={(e) => setNeu({ ...neu, kategorie: e.target.value })}
              >
                {Object.entries(KATEGORIE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-slate-700">Bezeichnung</label>
              <Input
                value={neu.bezeichnung ?? ""}
                onChange={(e) => setNeu({ ...neu, bezeichnung: e.target.value })}
              />
            </div>
            <p className="text-xs text-slate-500">
              Datei-Upload nach Paperless-ngx folgt in einem separaten Schritt (Stub, solange der
              Paperless-Zugang offen ist).
            </p>
          </form>
          <SheetFooter>
            <Button type="button" variant="secondary" onClick={() => setZeigeNeu(false)}>
              Abbrechen
            </Button>
            <Button type="submit" form="dokument-neu-form" disabled={anlegen.isPending}>
              {anlegen.isPending ? "Speichert…" : "Dokument speichern"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </section>
  )
}
